import { npcs } from '../data/guide/npcs'
import { recipes, recipePath } from '../data/guide/recipes'
import { characterClasses } from '../data/guide/classes'
import { publishedQuestIds, questExpectations, questTitle } from '../data/guide/quests'
import { adminConfirmation, formatAdminDate } from '../data/guide/adminConfirmations'
import { inGameTest, inGameTests } from '../data/guide/inGameTests'
import { COORD_KEY, NPC_ORIGIN_KEY, SERVER_SOURCE_KEY, SHOPS_SOURCE_KEY, SYSTEMS_SOURCE_KEY, TECHNICAL_AUDIT_LABEL, technicalAnswer } from '../data/guide/technicalAudit'
import { eventConfigLabel, eventStatusSummary, roomStatesText, serversText } from './eventStatus'
import type { MapImage } from './mapImages'
import { questsServedBy, unresolvedQuestNpcIds } from './npcQuests'
import { monsterKind } from './monsterKind'
import { shopOf } from './npcShops'
import { isSkillItem } from './skillItems'

/** Estado independiente de ubicación, función e inventario de cada NPC publicado. */
export type NpcStatusRow = { id: number; name: string; location: string; function: string; inventory: string }
import { EXPORT_DATE_LABEL } from '../data/world/package'
import { num, str, type Row } from './csv'
import { buildLookup } from './progressionLookup'
import { positionsFor, type EventPosition } from './eventPositions'
import { isPermanentSpawn, npcLocationText, resolveNpcLocation } from './npcLocation'
import { buildQuestView } from './questView'
import { itemCoverage, resolveItemSources } from './itemSources'
import { auditInvasions } from './invasionValidation'
import { coordLabel, itemName, itemPath, monsterPath } from './worldFormat'
import type { Material } from '../types/progression'
import type { WorldData } from '../types/world'

/*
 * Lista consolidada de TODO lo que falta confirmar en la guía, calculada con los archivos cargados.
 * Un pendiente = una pregunta. Si el mismo dato aparece en varias fichas, se pregunta una vez.
 * Usa la misma lógica que las fichas (resolveItemSources + itemCoverage).
 */

export type PendingCategory = 'ubicacion' | 'mision' | 'obtencion' | 'receta' | 'invasion' | 'evento' | 'mapa' | 'monstruo' | 'paquete' | 'sistema'
export type PendingReason = 'falta-dato' | 'conexion' | 'conflicto' | 'clasica' | 'en-revision'
export type Evidence = 'administración' | 'captura' | 'configuración' | 'prueba en el juego'

export type Pending = {
  key: string
  category: PendingCategory
  section: string
  links: string[]
  name: string
  missing: string
  known: string
  reason: PendingReason
  question: string
  evidence: Evidence[]
  affects?: number
  /** Necesita respuesta del administrador (no es un problema interno de conexión). */
  needsAdmin: boolean
  /** interna = la corrijo yo; archivo = falta una fuente adicional; administracion = decisión o confirmación del staff. */
  kind: PendingKind
  /** Claves originales unidas a esta pregunta (duplicados que se conservan como referencia). */
  refs?: string[]
  /** Solicitud de archivos (source:*) que resolverá este dato. El dato conserva su propio estado. */
  source?: string
}

/** Nombre corto de cada solicitud de fuentes. */
export const sourceLabel: Record<string, string> = {
  [NPC_ORIGIN_KEY]: 'NPC_ORIGEN — apariciones nativas de NPC',
  [SHOPS_SOURCE_KEY]: 'TIENDAS_SERVICIOS — tiendas y servicios de NPC',
  [SYSTEMS_SOURCE_KEY]: 'Archivos de sistemas',
  [SERVER_SOURCE_KEY]: 'Configuración y código del servidor',
}

export type PendingKind = 'interna' | 'archivo' | 'administracion'
export const kindLabel: Record<PendingKind, string> = {
  interna: 'Correcciones internas de la guía',
  archivo: 'Datos que requieren archivos adicionales',
  administracion: 'Decisiones de administración',
}

export type ProposedTest = { testId: string; what: string; how: string; record: string }
export type SectionCoverage = { section: string; reviewed: boolean; note: string }

/** Datos del paquete que el mundo cargado no incluye y se leen solo para este informe. */
export type PendingExtras = {
  eventMonsters?: Row[]
  eventSpawnRules?: Row[]
  /** Posiciones resueltas con utils/eventPositions (la misma lógica que las fichas). */
  positions?: EventPosition[]
  controlPending?: unknown
  /** 47_map_images: ejes declarados por mapa (coordinate_horizontal / coordinate_vertical). */
  mapImages?: Map<number, MapImage>
  errors: string[]
}

const categoryOrder: PendingCategory[] = ['ubicacion', 'mision', 'obtencion', 'receta', 'invasion', 'evento', 'mapa', 'monstruo', 'paquete', 'sistema']
export const categoryLabel: Record<PendingCategory, string> = {
  ubicacion: 'Ubicaciones de NPC',
  mision: 'Misiones y evoluciones',
  obtencion: 'Obtención de ítems',
  receta: 'Recetas, alas y capas',
  invasion: 'Invasiones',
  evento: 'Eventos',
  mapa: 'Accesos a mapas',
  monstruo: 'Monstruos y apariciones',
  paquete: 'Declarados en 00_control.csv',
  sistema: 'Sistemas y secciones sin datos',
}
export const reasonLabel: Record<PendingReason, string> = {
  'falta-dato': 'Falta el dato en los archivos',
  conexion: 'Relación entre archivos sin resolver',
  conflicto: 'Fuentes contradictorias',
  clasica: 'Referencia clásica sin validar en Vanguard',
  'en-revision': 'En revisión: datos presentes, publicación retenida',
}

const list = (xs: string[], n = 6) => (xs.length > n ? `${xs.slice(0, n).join(', ')} y ${xs.length - n} más` : xs.join(', '))
const isFalse = (v?: string) => /^(false|0)$/i.test(v ?? '')

/**
 * Fuente que resolverá cada dato. Recetas, efectos de misiones, combo y posiciones dentro de eventos dependen de
 * archivos o código del servidor: se agrupan en UNA solicitud técnica en vez de decenas de preguntas manuales.
 */
function sourceFor(key: string): string | undefined {
  if (key.startsWith('source:')) return undefined
  if (/^(recipe:|quest-reward:|combo:|event-positions:)/.test(key)) return SERVER_SOURCE_KEY
  if (key.startsWith('system:')) return SYSTEMS_SOURCE_KEY
  return undefined
}

type Gap = { monsterId: number; mapId?: number; monster: string; map: string; eventIds: Set<string>; items: Set<string> }

export function buildGuidePendings(w: WorldData, extras: PendingExtras = { errors: [] }) {
  const l = buildLookup(w)
  const out: Pending[] = []
  const resolvedByAdmin: { p: Pending; answer: string; date: string }[] = []
  const solvedFromData: string[] = []
  /** Resuelto por la auditoría técnica del paquete (procedencia propia: no es administración ni prueba en el juego). */
  const resolvedByAudit: { p: Pending; error: string; summary: string }[] = []
  /** Referencias diferidas: claves duplicadas que se unen a otra pregunta (se conservan como referencia). */
  const deferred: { into: string; ref?: string; text?: string }[] = []
  const attach = (into: string, ref?: string, text?: string) => deferred.push({ into, ref, text })
  /** Claves duplicadas que se responden en otra pregunta. */
  const duplicateOf: Record<string, string> = {
    'recipe:capas:rf': 'recipe:cloak-of-warrior',
  }
  const add = (p: Omit<Pending, 'needsAdmin' | 'kind'> & { needsAdmin?: boolean; kind?: PendingKind }) => {
    if (out.some((x) => x.key === p.key)) return
    if (duplicateOf[p.key]) {
      attach(duplicateOf[p.key], p.key)
      return
    }
    const source = p.source ?? sourceFor(p.key)
    const needsAdmin = p.needsAdmin ?? (source ? true : p.reason !== 'conexion' && p.evidence.includes('administración'))
    const full: Pending = { ...p, source, needsAdmin, kind: p.kind ?? (!needsAdmin ? 'interna' : p.key.startsWith('source:') || source ? 'archivo' : 'administracion') }
    const a = adminConfirmation(p.key)
    if (a) {
      resolvedByAdmin.push({ p: full, answer: a.answer, date: a.date })
      return
    }
    const t = technicalAnswer(p.key)
    if (t?.kind === 'resolved') {
      resolvedByAudit.push({ p: full, error: t.error, summary: t.summary })
      return
    }
    if (t?.kind === 'narrowed') {
      // Reemplaza el contenido original para que «Se sabe», «Falta» y la pregunta coincidan.
      full.known = `${t.known}${t.appendComputed ? ` Detalle calculado: ${p.known}` : ''} (${TECHNICAL_AUDIT_LABEL})`
      full.missing = t.missing
      full.question = t.question
      full.reason = full.reason === 'conflicto' && !/difiere|distinto/.test(t.known) ? 'falta-dato' : full.reason
      if (t.evidence) full.evidence = t.evidence
    }
    out.push(full)
  }
  const appendKnown = (key: string, text: string) => {
    const p = out.find((x) => x.key === key)
    if (p) p.known += ` ${text}`
    return !!p
  }
  const monsterName = (id: number) => w.monsters.get(id)?.name ?? `Monstruo ${id}`
  const mapName = (id?: number) => (id === undefined ? 'mapa sin indicar' : w.maps.get(id)?.name ?? `Mapa ${id}`)
  const eventName = (id: string) => w.events.get(id)?.name ?? id

  // ── 1. Ubicaciones de NPC ────────────────────────────────────────────────
  // Misma resolución que la tarjeta del NPC, las misiones, las recetas y el buscador (utils/npcLocation).
  // Causa documentada: exportadores/_mapas.py omite los NPC en location() y add_spawn(). Una sola solicitud (NPC_ORIGEN)
  // en vez de una pregunta por NPC; no es un error de descarga ni prueba de que no estén en el servidor.
  // Todos los NPC publicados del catálogo (misma clasificación que las fichas), incluido Robot (805).
  // Ubicación, función e inventario tienen estados independientes.
  const npcStatus: NpcStatusRow[] = Array.from(w.monsters.values())
    .filter((m) => monsterKind(w, m) === 'npc')
    .sort((a, b) => a.id - b.id)
    .map((m) => {
      const entries = npcs.filter((n) => n.monsterId === m.id)
      const locs = entries.map((n) => resolveNpcLocation(n, { status: 'ready', data: w }))
      const permanent = (w.idx.spawnsByMonster.get(m.id) ?? []).filter(isPermanentSpawn)
      // Mapa y coordenadas exactas con su procedencia (administración o configuración).
      const adminLocs = locs.filter((x) => x.state === 'admin')
      const configLocs = locs.filter((x) => x.state === 'config')
      const location: NpcStatusRow['location'] = adminLocs.length
        ? `confirmada por administración: ${adminLocs.map(npcLocationText).join(' | ')}`
        : configLocs.length
          ? `documentada en la configuración: ${configLocs.map(npcLocationText).join(' | ')}`
          : permanent.length
            ? `documentada en la configuración (08_spawns): ${permanent.map((s) => `${mapName(s.mapId)} ${coordLabel(s)}`).join(' | ')}`
            : locs.some((x) => x.state === 'reference')
              ? 'referencia clásica sin validar'
              : 'falta (NPC_ORIGEN)'
      const services = entries.flatMap((n) => n.services ?? [])
      const quests = questsServedBy(w, m.id)
      const fn: string[] = []
      if (quests.length) fn.push(`misiones ${quests.map((q) => q.questId).join('/')} (configuración, 37_quest_rules)`)
      services.forEach((s) => fn.push(`${s.kind} (${s.status === 'admin' ? 'administración' : s.status === 'clasica' ? 'clásica sin validar' : s.status})`))
      const sells = services.some((s) => s.kind === 'venta')
      const inventory = entries.some((n) => shopOf(n.id)?.items.length)
        ? 'documentado'
        : sells
          ? `falta (TIENDAS_SERVICIOS)${adminLocs.length ? ' — la ubicación y la función confirmadas no confirman el inventario' : ''}`
          : 'sin venta documentada'
      return { id: m.id, name: m.name, location, function: fn.length ? Array.from(new Set(fn)).join(' · ') : 'falta', inventory }
    })
  npcs.forEach((n) => {
    const loc = resolveNpcLocation(n, { status: 'ready', data: w })
    if (loc.state === 'config' || loc.state === 'admin') solvedFromData.push(`${n.name}: ${npcLocationText(loc)}.`)
  })
  const npcGaps = npcStatus.filter((r) => r.location.startsWith('falta') || r.location.startsWith('referencia'))
  const robot = npcStatus.find((r) => r.id === 805)
  if (npcGaps.length) {
    add({
      key: NPC_ORIGIN_KEY,
      category: 'ubicacion',
      section: 'NPC',
      links: ['/guia/npc'],
      name: `Ubicaciones de NPC (${npcGaps.length} de ${npcStatus.length} NPC publicados) — exportación NPC_ORIGEN`,
      missing: 'Exportación nativa de apariciones de NPC: monster_id, mapa, X/Y, dirección, subservidor y condiciones/rotación, respetando las secciones originales',
      known: `Revisados los ${npcStatus.length} NPC publicados del catálogo (estado por NPC en el anexo F). El exportador del paquete (exportadores/_mapas.py) excluye los NPC en location() y add_spawn(); por eso no hay filas de NPC en 08/09/31/32. Sin ubicación documentada: ${list(npcGaps.map((r) => `${r.name} (${r.id})`), 12)}.${robot ? ` Robot (805): ubicación ${robot.location}; función ${robot.function}; inventario ${robot.inventory}.` : ''} Las referencias clásicas siguen pendientes, nunca en verde.`,
      reason: 'falta-dato',
      question: 'Enviar la fuente nativa de apariciones de NPC del GameServer (solicitud NPC_ORIGEN) para integrar mapa, X/Y, subservidor y condiciones de cada instancia.',
      evidence: ['configuración'],
      needsAdmin: true,
      affects: npcGaps.length,
    })
  }
  add({
    key: SHOPS_SOURCE_KEY,
    category: 'ubicacion',
    section: 'NPC y tiendas',
    links: ['/guia/npc'],
    name: 'Tiendas y servicios de NPC — exportación TIENDAS_SERVICIOS',
    missing: 'Relación NPC/instancia → servicio/shop_id → inventario → item_id/variant_id, con nivel/opciones, cantidad, precio y moneda',
    known: 'Ninguno de los 47 CSV contiene inventarios ni la relación NPC–tienda. Las fichas muestran «Inventario de tienda pendiente de documentar», nunca «no vende nada».',
    reason: 'falta-dato',
    question: 'Enviar las tiendas del GameServer (archivos Shop y la relación NPC–tienda) para completar «Qué vende» y «Se compra en».',
    evidence: ['configuración'],
    needsAdmin: true,
  })
  const badQuestNpcs = unresolvedQuestNpcIds(w)
  if (badQuestNpcs.length) {
    add({ key: 'quest-npc-fk', category: 'mision', section: 'Evoluciones', links: ['/guia/verificacion#progresion'], name: '37_quest_rules.npc_monster_id sin NPC en 06_monsters', missing: 'NPC de la misión', known: `IDs: ${badQuestNpcs.join(', ')}.`, reason: 'conexion', question: 'Relación interna: npc_monster_id no existe en 06_monsters.', evidence: ['configuración'] })
  } else if (w.quests.rules.length) {
    solvedFromData.push('37_quest_rules.npc_monster_id: todos existen en 06_monsters (Sebina 235 → 0/1, Marlon 229 → 2/3, Devin 406 → 4/5/6 según el archivo).')
  }

  // ── 2. Misiones ──────────────────────────────────────────────────────────
  const gaps = new Map<string, Gap>()
  const addGap = (monsterId: number, mapId: number | undefined, eventId: string | undefined, item: string) => {
    const k = `${monsterId}|${mapId ?? ''}`
    const g = gaps.get(k) ?? { monsterId, mapId, monster: monsterName(monsterId), map: mapName(mapId), eventIds: new Set<string>(), items: new Set<string>() }
    if (eventId) g.eventIds.add(eventId)
    g.items.add(item)
    gaps.set(k, g)
  }
  publishedQuestIds.forEach((qid) => {
    const v = buildQuestView(w, qid)
    const title = questTitle(qid)
    const path = qid <= 1 ? '/guia/evoluciones#sebina' : qid <= 3 ? '/guia/evoluciones#marlon' : '/guia/evoluciones#tercera'
    if (!v) {
      add({ key: `quest-missing:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: 'La misión completa', known: 'No figura en 36_quests.', reason: 'conexion', question: `Relación interna: la misión ${qid} no aparece en 36_quests.`, evidence: ['configuración'] })
      return
    }
    const exp = questExpectations[qid]
    if (exp && v.levelMin !== undefined && exp.levelMin !== v.levelMin) {
      add({ key: `quest-level-conflict:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: 'Nivel mínimo correcto', known: `Archivos: ${v.levelMin}. Referencia del staff: ${exp.levelMin}.`, reason: 'conflicto', question: `¿Cuál es el nivel mínimo vigente para ${title}: ${v.levelMin} o ${exp.levelMin}?`, evidence: ['administración', 'prueba en el juego'] })
    }
    if (exp && v.zen !== undefined && exp.zen !== v.zen) {
      add({ key: `quest-zen-conflict:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: 'Zen correcto', known: `Archivos: ${v.zen}. Referencia del staff: ${exp.zen}.`, reason: 'conflicto', question: `¿Cuánto Zen cuesta hoy ${title}: ${v.zen} o ${exp.zen}?`, evidence: ['administración', 'prueba en el juego'] })
    }
    const reqMissing = [v.levelMin === undefined && 'nivel mínimo', v.zen === undefined && 'Zen'].filter(Boolean).join(' y ')
    if (reqMissing) {
      add({ key: `quest-req:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: reqMissing, known: 'La misión existe en 36_quests/37_quest_rules sin ese valor.', reason: 'falta-dato', question: `¿Qué ${reqMissing} exige ${title}?`, evidence: ['administración', 'configuración'] })
    }
    v.kills.forEach((k) => {
      if (!k.resolved || k.quantity === undefined) {
        add({ key: `quest-kill:${qid}:${k.objective.id}`, category: 'mision', section: 'Evoluciones', links: [path], name: `${title}: ${k.name}`, missing: [!k.resolved && 'qué monstruo es', k.quantity === undefined && 'cantidad de bajas'].filter(Boolean).join(' y '), known: `Objetivo ${k.objective.id} de 39_quest_objectives.`, reason: k.resolved ? 'falta-dato' : 'conexion', question: k.resolved ? `En ${title}, ¿cuántas veces hay que derrotar a ${k.name}?` : `Relación interna: el monstruo del objetivo ${k.objective.id} no se resuelve en 06_monsters.`, evidence: k.resolved ? ['administración', 'configuración'] : ['configuración'] })
      }
    })
    v.items.forEach((it) => {
      const id = it.variantId ?? it.itemId
      if (it.quantity === undefined) {
        add({ key: `quest-item-qty:${qid}:${it.itemId}`, category: 'mision', section: 'Evoluciones', links: [path, itemPath(id)], name: `${title}: ${it.name}`, missing: 'Cantidad requerida', known: `Ítem ${id} en 39_quest_objectives sin cantidad.`, reason: 'falta-dato', question: `¿Cuántas unidades de ${it.name} pide ${title}?`, evidence: ['administración', 'configuración'] })
      }
      if (!it.drop || !it.drop.targets.length) {
        add({ key: `quest-item-source:${id}`, category: 'mision', section: 'Evoluciones', links: [path, itemPath(id)], name: it.name, missing: 'Monstruo y mapa donde se consigue con la misión activa', known: it.drop ? 'Tiene regla en 41_quest_drop_rules sin objetivos en 42 ni en 11_route_targets.' : 'Sin regla en 41_quest_drop_rules.', reason: it.drop ? 'conexion' : 'falta-dato', question: it.drop ? `Relación interna: la regla de drop de ${it.name} no tiene objetivos.` : `¿Qué monstruos sueltan ${it.name} durante ${title} y en qué mapas?`, evidence: it.drop ? ['configuración'] : ['administración', 'configuración'] })
      } else {
        it.drop.targets.filter((t) => !t.spawns.length).forEach((t) => addGap(t.monsterId, t.mapId, undefined, `${it.name} (misión)`))
      }
    })
  })
  ;([
    ['quest-reward:sebina', 'Sebina (misiones 0 y 1)', '/guia/evoluciones#sebina', '¿Qué entrega completar la misión de Sebina en Vanguard (cambio a segunda clase, otros premios)?'],
    ['quest-reward:marlon', 'Marlon, primera parte (misión 2)', '/guia/evoluciones#marlon', '¿Qué beneficio da completar Marlon en Vanguard (puntos por nivel u otro) y desde qué nivel aplica?'],
    ['quest-reward:devin', 'Tercera evolución (misiones 4–6)', '/guia/evoluciones#tercera', '¿Qué entrega cada etapa de Devin y en cuál se obtiene la tercera clase?'],
  ] as const).forEach(([key, name, path, question]) => add({ key, category: 'mision', section: 'Evoluciones', links: [path], name, missing: 'Recompensa o efecto de la misión', known: 'Requisitos y objetivos están en los archivos; ningún archivo del paquete contiene recompensas de misión.', reason: 'falta-dato', question, evidence: ['administración', 'prueba en el juego'] }))
  add({ key: 'combo:bk', category: 'mision', section: 'Evoluciones', links: ['/guia/evoluciones#combo', 'buscador ("combo bk")'], name: 'Combo del Blade Knight (misión 3)', missing: 'Secuencia de habilidades y condiciones del combo', known: 'Requisitos de la misión 3 en los archivos. La secuencia no está en ningún archivo.', reason: 'falta-dato', question: '¿Qué secuencia de habilidades activa el combo en Vanguard, con qué tiempos, y aplica solo al Blade Knight?', evidence: ['administración', 'prueba en el juego'] })
  add({ key: 'quest-unpublished:7-9', category: 'mision', section: 'Evoluciones', links: ['/guia/verificacion#progresion'], name: 'Misiones 7, 8 y 9', missing: 'Qué son y si deben publicarse', known: 'Existen en los archivos de misiones sin título editorial; el panel de misiones muestra sus referencias.', reason: 'falta-dato', question: '¿Qué misiones son la 7, 8 y 9, están activas y deben aparecer en la guía?', evidence: ['administración'] })
  characterClasses.forEach((c) => {
    if (!c.sebinaItem) return
    const id = l.identify(c.sebinaItem)
    if (!id) add({ key: `name:${c.sebinaItem.gameName}`, category: 'mision', section: 'Evoluciones', links: ['/guia/evoluciones#sebina'], name: `${c.sebinaItem.gameName} (${c.base})`, missing: 'Nombre e identificador en el catálogo', known: 'Probado por ID, nombre, alias y variantes sin coincidencia.', reason: 'falta-dato', question: `¿Con qué nombre exacto (o item_id) figura en Vanguard el objeto de Sebina para ${c.base} (clásico: ${c.sebinaItem.gameName})?`, evidence: ['administración', 'configuración'] })
    else solvedFromData.push(`${c.sebinaItem.gameName}: identificado como ${id.name} (${id.id}) por ${id.matchedBy}.`)
  })

  // ── 3. Recetas (cada variante por separado) ──────────────────────────────
  // Trainer (226): el staff confirmó que "Pet Trainer" y "Trainer" son el mismo NPC; su ubicación se
  // resuelve en el bloque 1 como cualquier otro NPC (apariciones permanentes de 226 o referencia).
  solvedFromData.push('Trainer (226): confirmado por el staff como el NPC que resucita Spirit of Dark Horse y Spirit of Dark Raven ("Pet Trainer" es el mismo NPC).')
  // Capas: existencia y compatibilidad salen de 01_items y 05_item_classes; solo se pregunta por la receta.
  const capeInfo = (id: string) => {
    const it = w.items.get(id)
    const cls = Array.from(new Set(w.itemClasses.get(id) ?? [])).map((c) => w.classNames.get(c) ?? `clase ${c}`)
    return it ? `${it.name} (${id})${cls.length ? ` · 05_item_classes: ${cls.join(', ')}` : ' · sin filas en 05_item_classes'}` : `${id} no encontrado en 01_items`
  }
  solvedFromData.push(`Capas documentadas: ${['12,40', '12,50', '12,49'].map(capeInfo).join(' | ')}.`)
  add({
    key: 'recipe:capes-third',
    category: 'receta',
    section: 'Alas y capas',
    links: [recipePath('alas-evo-3'), itemPath('12,40'), itemPath('12,50')],
    name: 'Receta de las capas de tercera generación (Cape of Emperor y Lord of the Cape)',
    missing: 'Receta y condiciones: ingredientes, mínimos, Zen, probabilidad, fallo y si sale de la mezcla de alas Evo 3',
    known: `Existencia y compatibilidad documentadas: ${capeInfo('12,40')}; ${capeInfo('12,50')}.`,
    reason: 'clasica',
    question: '¿Cape of Emperor (12,40, Lord Emperor) y Lord of the Cape (12,50, Fist Master) se crean con la misma mezcla de alas Evo 3? ¿Qué ingredientes, mínimos, Zen, probabilidad y efectos del fallo tiene cada una?',
    evidence: ['administración', 'configuración', 'prueba en el juego'],
  })
  add({
    key: 'recipe:cloak-of-warrior',
    category: 'receta',
    section: 'Alas y capas',
    links: [recipePath('capas'), itemPath('12,49')],
    name: 'Receta de Cloak of Warrior (12,49)',
    missing: 'Cómo se obtiene o crea y con qué condiciones',
    known: `Documentado: ${capeInfo('12,49')}.`,
    reason: 'clasica',
    question: '¿Cloak of Warrior (12,49) se crea en la Chaos Machine? Si sí: ingredientes, mínimos, Zen, probabilidad y efectos del fallo. No se asume que use la receta de la capa del Dark Lord.',
    evidence: ['administración', 'configuración', 'prueba en el juego'],
  })
  // Paquetes de joyas: identidad ya resuelta (auditoría técnica: 10 paquetes). Solo queda el mecanismo y las cantidades.
  const packCandidates = Array.from(w.items.values()).filter((it) => /(bundle|pack|package|paquete)/i.test(it.name) && /(jewel|joya|bless|soul|life|chaos|creation|guardian)/i.test(it.name))
  add({
    key: 'recipe:jewel-packs',
    category: 'receta',
    section: 'Mascotas y combinaciones',
    links: ['/guia/combinaciones', ...packCandidates.slice(0, 2).map((it) => itemPath(it.id))],
    name: 'Paquetes de joyas: mecanismo y cantidades',
    missing: 'Mecanismo de empaquetado (NPC o máquina), cantidad de joyas de cada paquete y sus condiciones',
    known: `Los 10 paquetes de joyas están identificados en el catálogo y algunos tienen vías de recompensa (${TECHNICAL_AUDIT_LABEL})${packCandidates.length ? `. En 01_items: ${list(packCandidates.map((it) => `${it.name} (${it.id})`), 10)}` : ''}. No se asume que cada paquete contenga 10/20/30.`,
    reason: 'falta-dato',
    question: '¿Cómo se arman y desarman los paquetes de joyas en Vanguard (NPC o máquina), cuántas joyas lleva cada uno y con qué condiciones (Zen, nivel)?',
    evidence: ['administración', 'configuración'],
  })
  const craftKinds = Array.from(new Set(w.routes.map((r) => r.kind))).filter((k) => /mix|craft|chaos|combin|recipe/i.test(k))
  const recipeData = craftKinds.length ? `Hay rutas de tipo ${craftKinds.join(', ')} en 10_drop_routes por revisar.` : 'Ningún archivo del paquete contiene recetas de la Chaos Machine.'
  const unknownNames = new Map<string, string[]>()
  const checkMat = (m: Material, where: string) => {
    if (!m.gameName && !m.itemId) return
    if (!l.identify(m)) unknownNames.set(m.gameName ?? m.itemId!, [...(unknownNames.get(m.gameName ?? m.itemId!) ?? []), where])
  }
  const matText = (mats: Material[]) => mats.map((m) => `${m.qty ?? '?'} × ${m.gameName ?? m.esName}${m.requirement ? ` (${m.requirement})` : ''}`).join('; ')
  recipes.forEach((r) => {
    const units = r.variants?.length
      ? r.variants.map((v) => ({ key: `recipe:${r.id}:${v.id}`, title: v.title, mats: v.ingredients, results: v.results, extra: v.note }))
      : [{ key: `recipe:${r.id}`, title: r.title, mats: r.ingredients, results: r.results, extra: undefined as string | undefined }]
    units.forEach((u) => {
      u.mats.forEach((m) => checkMat(m, u.title))
      add({
        key: u.key,
        category: 'receta',
        section: !r.group || r.group === 'alas' ? 'Alas y capas' : 'Mascotas y combinaciones',
        links: [recipePath(r.id), !r.group || r.group === 'alas' ? '/guia/alas' : '/guia/combinaciones', 'buscador'],
        name: `Receta: ${u.title}`,
        missing: `Por separado: ingredientes y cantidades vigentes, Zen, probabilidad de éxito, efectos del fallo, cambio por VIP y NPC que realiza la combinación (${r.machine ?? 'Chaos Machine'})`,
        known: `Referencia clásica: ${matText(u.mats) || 'sin ingredientes cargados'}.${u.extra ? ` ${u.extra}` : ''} ${recipeData}`,
        reason: 'clasica',
        question:
          r.id === 'capas' && u.key.endsWith(':rf')
            ? '¿Qué receta usa la capa del Rage Fighter en Vanguard (ingredientes, mínimos, Zen, éxito y fallo)? No se asume que sea la misma del Dark Lord.'
            : `Para "${u.title}" en Vanguard: 1) ¿se mantienen estos ingredientes y cantidades? 2) ¿cuánto Zen cuesta? 3) ¿cuál es la probabilidad de éxito? 4) ¿qué se pierde si falla? 5) ¿el VIP cambia la probabilidad? 6) ¿la hace ${r.machine ?? 'la Chaos Machine'}?`,
        evidence: ['administración', 'configuración', 'prueba en el juego'],
      })
      u.results.forEach((res) => {
        if (l.identify(res)) return
        unknownNames.set(res.gameName, [...(unknownNames.get(res.gameName) ?? []), `${u.title} (resultado)`])
      })
    })
  })
  unknownNames.forEach((where, name) => add({ key: `name:${name}`, category: 'receta', section: 'Alas y capas', links: ['/guia/alas', 'buscador'], name, missing: 'Nombre e identificador en el catálogo', known: `Usado en: ${list(Array.from(new Set(where)))}. Sin coincidencia por ID, nombre, alias ni variante.`, reason: 'falta-dato', question: `¿Con qué nombre exacto o item_id figura "${name}" en el catálogo de Vanguard, o no existe en el servidor?`, evidence: ['administración', 'configuración'] }))

  // ── 4. Obtención de ítems (misma lógica que las fichas) ──────────────────
  const byReason = new Map<string, { ids: string[]; names: string[]; reason: string; detail: string }>()
  const noRoutes: string[] = []
  const reviewItems: string[] = []
  const coordItems: string[] = []
  w.items.forEach((it) => {
    const sources = resolveItemSources(w, it.id)
    const c = itemCoverage(sources)
    const walkTargets = (ss: typeof sources) =>
      ss.filter((s) => !s.blocked && s.status !== 'disabled').forEach((s) => {
        s.targets.filter((t) => !t.spawns.length).forEach((t) => addGap(t.monsterId, t.mapId, t.eventId, it.name))
      })
    walkTargets(sources)
    if (c.coverage === 'no-routes') noRoutes.push(`${it.name} (${it.id})`)
    else if (c.coverage === 'review') reviewItems.push(`${it.name} (${it.id})`)
    else if (c.coverage === 'partial' && c.detail === 'coords') coordItems.push(`${it.name} (${it.id})`)
    else if (c.coverage === 'partial') {
      const pattern = (c.reason ?? '—').replace(/route:\w+|rule:\d+|\d+,\d+(@\d+)?|\d+ objetivos|drop_level del ítem [\d—]+|\(.*?\)/g, '…')
      const g = byReason.get(pattern) ?? { ids: [], names: [], reason: c.reason ?? '—', detail: c.detail ?? 'missing' }
      g.ids.push(it.id)
      g.names.push(it.name)
      byReason.set(pattern, g)
    }
  })
  Array.from(byReason.entries()).sort((a, b) => b[1].ids.length - a[1].ids.length).forEach(([pattern, g], i) => {
    const disabledBox = /desactivadas por configuración/.test(g.reason)
    const noBoxRoutes = /no tiene rutas en 10_drop_routes/.test(g.reason)
    const internal = /inexistente|no existe|sin filas en route_targets.*sin via_item_ids/i.test(g.reason)
    add({
      key: `items-partial:${g.detail}:${i}`,
      category: 'obtencion',
      section: 'Ítems y drops',
      links: g.ids.slice(0, 3).map(itemPath),
      name: `${g.ids.length} ítems: ${pattern.slice(0, 90)}`,
      missing: disabledBox ? 'Si la caja intermedia se puede conseguir hoy' : noBoxRoutes ? 'Cómo se consigue la caja intermedia' : 'Monstruo, evento o caja de origen',
      known: `Ejemplos: ${list(g.names)}. Motivo calculado: ${g.reason}`,
      reason: internal ? 'conexion' : 'falta-dato',
      question: internal
        ? 'Relación interna entre archivos: la reviso con el diagnóstico.'
        : disabledBox
          ? `La caja de la que salen ${list(g.names, 3)} solo tiene rutas desactivadas en la configuración. ¿Esa caja se consigue hoy de alguna otra forma, o estos ítems no se obtienen por esa vía?`
          : `¿Cómo se obtienen en Vanguard ${list(g.names, 4)}? (lista completa en el anexo)`,
      evidence: internal ? ['configuración'] : ['administración', 'configuración'],
      affects: g.ids.length,
    })
  })
  if (noRoutes.length) {
    add({ key: 'items-no-routes', category: 'obtencion', section: 'Ítems y drops', links: ['/guia/items'], name: `${noRoutes.length} ítems publicados sin ruta de drop en los archivos`, missing: 'Si se obtienen por otra vía (tienda, NPC, cash shop, recompensa, receta) o no están disponibles', known: `Ejemplos: ${list(noRoutes)}. Sin filas en 10_drop_routes. "Sin ruta de drop" no significa "no disponible en el servidor".`, reason: 'falta-dato', question: '¿Estos ítems se consiguen de otra forma en Vanguard (tienda, NPC, cash shop, evento, receta) o no están disponibles en el servidor? Lista completa en el anexo A.', evidence: ['administración'], affects: noRoutes.length })
  }

  // ── 5. Monstruo + mapa sin coordenadas, por contexto (31/32) ─────────────
  const evMon = extras.eventMonsters ?? []
  const evRules = extras.eventSpawnRules ?? []
  const byEvent = new Map<string, { gaps: Gap[]; inactive: boolean }>()
  const ordinaryGaps: Gap[] = []
  let solvedBy32 = 0
  const allPositions = extras.positions ?? []
  gaps.forEach((g) => {
    // Misma resolución que las fichas: monstruo + mapa + evento del objetivo (si lo tiene).
    const evs = g.eventIds.size ? Array.from(g.eventIds) : [undefined]
    if (evs.some((eid) => positionsFor(allPositions, g.monsterId, g.mapId, eid).length)) {
      solvedBy32++
      return
    }
    const mons = evMon.filter((r) => num(r.monster_id) === g.monsterId && (g.mapId === undefined || num(r.map_id) === undefined || num(r.map_id) === g.mapId))
    const evIds = new Set<string>([...g.eventIds, ...mons.map((r) => str(r.event_id)).filter((x): x is string => !!x)])
    if (!evIds.size) {
      ordinaryGaps.push(g)
      return
    }
    evIds.forEach((eid) => {
      const e = byEvent.get(eid) ?? { gaps: [], inactive: false }
      e.gaps.push(g)
      // Estado del EVENTO: solo 27_events.active_configured. Filas, salas o subservidores no lo cambian.
      if (w.events.get(eid)?.activeConfigured === false) e.inactive = true
      byEvent.set(eid, e)
    })
  })
  if (solvedBy32) solvedFromData.push(`${solvedBy32} pares monstruo + mapa sin aparición permanente tienen posición en 32_event_spawn_rules; las fichas las muestran como "Posición dentro del evento", no como spot permanente.`)
  // Monstruos de evento SIN drops ni misión (p. ej. Spider Eggs 460–462 en Raklion). Corrección interna: antes esta
  // revisión solo partía de objetivos de drop/misión (gaps), así que los monstruos que no sueltan nada quedaban fuera.
  const evExtra = new Map<string, { label: string; inactive: boolean }[]>()
  evMon.forEach((r) => {
    const eid = str(r.event_id)
    const mid = num(r.monster_id)
    if (!eid || mid === undefined) return
    const mapId = num(r.map_id)
    if (positionsFor(allPositions, mid, mapId, eid).length) return
    if (byEvent.get(eid)?.gaps.some((g) => g.monsterId === mid)) return
    const phase = str(r.phase)
    const label = `${w.monsters.has(mid) ? monsterName(mid) : `ID ${mid} (no está en 06_monsters)`} (${mid})${mapId !== undefined ? ` · ${mapName(mapId)}` : ''}${phase ? ` · fase ${phase}` : ''}`
    const arr = evExtra.get(eid) ?? []
    if (!arr.some((x) => x.label === label)) arr.push({ label, inactive: isFalse(r.active) })
    evExtra.set(eid, arr)
  })
  const dataChecks: string[] = []
  const raklion = [459, 460, 461, 462].map((id) => {
    const rows = evMon.filter((r) => num(r.monster_id) === id)
    const m = w.monsters.get(id)
    const ctx = Array.from(new Set(rows.map((r) => `${eventName(str(r.event_id) ?? '?')} · ${mapName(num(r.map_id))}${str(r.phase) ? ` · fase ${str(r.phase)}` : ''}`)))
    return `${m ? m.name : 'no está en 06_monsters'} (${id}): ${m ? `clasificación ${monsterKind(w, m)}, visible en lista y buscador` : 'sin ficha'}; 31_event_monsters: ${rows.length} filas${ctx.length ? ` (${ctx.join('; ')})` : ''}; posiciones en 32: ${allPositions.filter((p) => p.monsterId === id).length}`
  })
  dataChecks.push(`Raklion (Selupan y Spider Eggs): ${raklion.join(' | ')}. Ningún filtro de publicación los oculta; la revisión de posiciones antes solo incluía monstruos con drops o misión, por eso los huevos no aparecían.`)
  new Set([...byEvent.keys(), ...evExtra.keys()]).forEach((eid) => {
    const e = byEvent.get(eid) ?? { gaps: [], inactive: false }
    const extra = evExtra.get(eid) ?? []
    const inactive = e.inactive
    const inactiveRows = extra.filter((x) => x.inactive).length
    const labels = [...e.gaps.map((g) => `${g.monster} (${g.map})`), ...extra.map((x) => `${x.label}${x.inactive ? ' [fila inactiva en 31]' : ''}`)]
    add({
      key: `event-positions:${eid}`,
      category: 'monstruo',
      section: 'Eventos',
      links: ['/guia/eventos'],
      name: `${eventName(eid)}: ${labels.length} monstruos sin posición`,
      missing: 'Solo posiciones, áreas o regla de aparición dentro del evento (mapa y fase ya documentados)',
      known: `Mapa y fase documentados en 31_event_monsters: ${list(labels, 10)}. Sin coordenadas en 08_spawns ni regla en 32_event_spawn_rules (32 solo trae 64 reglas de invasiones; 09_monster_locations documenta presencia, no X/Y). Evento: ${inactive ? 'deshabilitado' : 'no deshabilitado'} según 27_events.active_configured.${inactiveRows ? ` ${inactiveRows} filas de 31 figuran inactivas; no cambian el estado del evento.` : ''}`,
      reason: 'falta-dato',
      question: `Posiciones, áreas o regla de aparición (fija, área o aleatoria por fase) de estos monstruos dentro de ${eventName(eid)}, desde la configuración de eventos del servidor.`,
      evidence: ['administración', 'configuración'],
      affects: labels.length,
    })
  })
  if (ordinaryGaps.length) {
    add({ key: 'coords-missing', category: 'monstruo', section: 'Mapas y spots', links: ordinaryGaps.slice(0, 3).map((g) => monsterPath(g.monsterId)), name: `${ordinaryGaps.length} monstruos + mapa sin coordenadas ni evento asociado`, missing: 'Coordenadas o condición de aparición', known: `Ejemplos: ${list(ordinaryGaps.map((g) => `${g.monster} en ${g.map}`))}. Tienen drops documentados; no figuran en 08_spawns, 31 ni 32 para ese mapa.`, reason: 'falta-dato', question: '¿Estos monstruos aparecen en esos mapas (spot fijo, invocación, otro evento)? Si tienen spot, ¿en qué coordenadas? (anexo D)', evidence: ['administración', 'configuración'], affects: ordinaryGaps.length })
  }
  const unplaced = Array.from(w.monsters.values()).filter((m) => (w.idx.routeTargetsByMonster.get(m.id)?.length ?? 0) > 0 && !w.idx.spawnsByMonster.get(m.id)?.length && !w.idx.locationsByMonster.get(m.id)?.length && !evMon.some((r) => num(r.monster_id) === m.id))
  if (unplaced.length) {
    add({ key: 'monsters-unplaced', category: 'monstruo', section: 'Monstruos', links: unplaced.slice(0, 3).map((m) => monsterPath(m.id)), name: `${unplaced.length} monstruos con drops pero sin ubicación ni evento`, missing: 'Dónde aparecen', known: `Ejemplos: ${list(unplaced.map((m) => `${m.name} (${m.id})`))}. En 11_route_targets; no en 08, 09 ni 31.`, reason: 'falta-dato', question: '¿Dónde aparecen estos monstruos (mapa, evento o invocación), o están desactivados en Vanguard?', evidence: ['administración', 'configuración'], affects: unplaced.length })
  }

  // ── 6. Invasiones ────────────────────────────────────────────────────────
  auditInvasions(w).forEach((a) => {
    if (!a.unlisted && !a.conflict) return
    const unlistedNames = Array.from(new Set(a.rows.filter((r) => r.verdict === 'unlisted').map((r) => r.name)))
    const conflictNames = Array.from(new Set(a.rows.filter((r) => r.verdict === 'conflict').map((r) => r.name)))
    add({
      key: `invasion:${a.policy.guideEntryId}`,
      category: 'invasion',
      section: 'Invasiones',
      links: ['/guia/invasiones-bosses/invasiones', '/guia/eventos'],
      name: `Recompensas de ${a.policy.name}`,
      missing: a.policy.missing ?? 'Lista vigente de ítems (item_id + nivel)',
      known: `Aprobado: ${a.policy.approved} Exportación: ${a.items} ítems · ${a.authorized} autorizados · ${a.unlisted} sin lista vigente${unlistedNames.length ? ` (${list(unlistedNames, 4)})` : ''} · ${a.conflict} en conflicto${conflictNames.length ? ` (${list(conflictNames, 4)})` : ''}.`,
      reason: a.conflict ? 'conflicto' : 'en-revision',
      question: a.conflict
        ? `¿Cuál es la lista exacta (nombre, nivel, calidad) que entrega hoy ${a.policy.name}? Los ítems en conflicto (${list(conflictNames, 4)}), ¿siguen en el servidor o la guía aprobada es la vigente?`
        : `¿Cuál es la lista exacta (nombre, nivel, calidad) que entrega hoy ${a.policy.name}? ¿Se pueden publicar ${list(unlistedNames, 4)}?`,
      evidence: ['administración', 'configuración'],
      affects: a.unlisted + a.conflict,
    })
  })

  // ── 7. Eventos ───────────────────────────────────────────────────────────
  // Evento, subservidor y sala con estados separados (utils/eventStatus, la misma lógica que /guia/eventos).
  dataChecks.push('Corrección interna: un evento ya no se marca inactivo porque alguna fila de 31, una sala o un subservidor esté deshabilitado; su estado sale solo de 27_events.active_configured.')
  w.events.forEach((e) => {
    const s = eventStatusSummary(w, e)
    if (!s.rooms.some((r) => r.state !== 'activa') && !s.servers.some((x) => !x.enabled) && !/devil|chaos castle/i.test(e.name)) return
    dataChecks.push(
      `${e.name} (${e.id}): evento ${eventConfigLabel[s.event].toLowerCase()} (27_events.active_configured)${s.servers.length ? `; subservidores: ${serversText(s.servers)}` : ''}${s.rooms.length ? `; ${roomStatesText(s.rooms)} (33_event_rooms)` : ''}. Configuración del ${EXPORT_DATE_LABEL}; no confirma la versión desplegada hoy.`,
    )
  })
  w.events.forEach((e) => {
    const schedules = w.idx.schedulesByEvent.get(e.id) ?? []
    const rooms = w.idx.roomsByEvent.get(e.id) ?? []
    const base = { category: 'evento' as const, section: 'Eventos', links: ['/guia/eventos'], name: `${e.name} (${e.id})`, evidence: ['administración', 'configuración'] as Evidence[] }
    if (e.activeConfigured === false && e.hasAutomaticSchedule === false) {
      add({ ...base, key: `event-manual:${e.id}`, missing: 'Si se usa de forma manual o debe mostrarse como inactivo', known: '27_events: active_configured = false, has_automatic_schedule = false.', reason: 'falta-dato', question: `${e.name}: ¿el staff lo activa manualmente, o debe seguir presentado como inactivo en la guía?` })
      return
    }
    if (/trigger/i.test(e.timingType ?? '')) {
      const ask = [!rooms.some((r) => r.levelMin !== undefined) && 'requisitos de entrada (nivel, ítem o misión)', 'qué lo activa', 'cada cuánto puede volver a abrirse'].filter(Boolean) as string[]
      add({ ...base, key: `event-trigger:${e.id}`, missing: ask.join(', '), known: `27_events: timing_type = ${e.timingType} (evento por activación, sin horario fijo)${rooms.length ? `; ${rooms.length} salas con niveles en 33_event_rooms` : ''}.`, reason: 'falta-dato', question: `${e.name} se activa por condición, no por horario: ¿${ask.join(', ')}?` })
      return
    }
    const missing: string[] = []
    if (e.activeConfigured === undefined) missing.push('si está activo')
    if (!schedules.length && !e.hasAutomaticSchedule && !e.dailyOccurrences) missing.push('horario (días y horas)')
    if (missing.length) add({ ...base, key: `event:${e.id}`, missing: missing.join(' y '), known: `En 27_events${e.timingType ? ` (timing_type ${e.timingType})` : ''}.`, reason: 'falta-dato', question: `${e.name}: ¿${missing.join('? ¿')}?` })
  })

  // ── 8. Accesos a mapas ───────────────────────────────────────────────────
  const eventMapIds = new Set(Array.from(w.events.values()).flatMap((e) => e.possibleMapIds))
  const noAccess: string[] = []
  const partialAccess: string[] = []
  const eventOnly: string[] = []
  w.maps.forEach((m) => {
    if (!m.moveEntries.length && m.minimumMoveLevel === undefined) (eventMapIds.has(m.id) ? eventOnly : noAccess).push(m.name)
    else if (m.moveEntries.some((e) => e.minLevel === undefined) && m.minimumMoveLevel === undefined) partialAccess.push(m.name)
  })
  if (noAccess.length) add({ key: 'map-access:none', category: 'mapa', section: 'Mapas', links: ['/guia/mapas'], name: `${noAccess.length} mapas sin acceso documentado`, missing: 'Cómo se entra y con qué requisito', known: `Sin minimum_move_level ni move_entries en 07_maps: ${list(noAccess, 10)}.`, reason: 'falta-dato', question: `¿Cómo se entra a ${list(noAccess, 6)} (portal, NPC, /move) y con qué nivel, misión o ítem?`, evidence: ['administración', 'configuración'], affects: noAccess.length })
  if (partialAccess.length) add({ key: 'map-access:partial', category: 'mapa', section: 'Mapas', links: ['/guia/mapas'], name: `${partialAccess.length} mapas con accesos sin nivel`, missing: 'Nivel mínimo de cada acceso', known: `Tienen move_entries sin nivel: ${list(partialAccess, 10)}.`, reason: 'falta-dato', question: `¿Qué nivel mínimo exige cada acceso de ${list(partialAccess, 6)}?`, evidence: ['administración', 'configuración'], affects: partialAccess.length })
  if (eventOnly.length) solvedFromData.push(`${eventOnly.length} mapas sin /move son mapas de evento (27_events); su acceso es el del evento: ${list(eventOnly, 8)}.`)

  // ── 9. Pendientes declarados en 00_control.csv ───────────────────────────
  // Cada uno se une a la pregunta concreta que ya existe (se conserva su clave como referencia).
  const ctl = normalizeControlPending(extras.controlPending)
  const controlTests: ProposedTest[] = []
  ctl.forEach((c) => {
    const t = `${c.id} ${c.title ?? ''} ${c.text}`.toLowerCase()
    const tag = `[Declarado también en 00_control.csv: ${c.id}]`
    const ref = `control:${c.id}`
    if (/red.?dragon/.test(t)) {
      attach('invasion:red-dragon', ref)
      return
    }
    if (/bonus|happy|doble/.test(t)) {
      attach('system:bonus', ref)
      return
    }
    if (/prueba|\breal\b|prueba_real|in.?game|dentro del juego|\btest/.test(t)) {
      controlTests.push(
        { testId: `${ref}:navegador`, what: 'Carga completa en el navegador', how: 'Abrir /guia/verificacion → Paquete y comprobar que los 47 archivos cargan con sus filas', record: 'Archivos cargados / esperados y errores.' },
        { testId: `${ref}:npc`, what: 'Fichas de NPC representativos', how: 'Abrir Sebina 235, Marlon 229, Devin 406, Robot 805, Hanzo 251, Pasi 254, Elf Lala 242 y Eo 243', record: 'Ubicación, función e inventario mostrados frente al estado del anexo F.' },
        { testId: `${ref}:werewolf`, what: 'Werewolf Quarel 407 frente a Werewolf 313/420', how: 'Buscar "werewolf" y abrir los tres resultados', record: '407 como NPC; 313 y 420 como monstruos.' },
        { testId: `${ref}:golden`, what: 'Dorados: directo e indirecto', how: 'Abrir /guia/dorados y una ficha de Box of Kundun', record: 'Que el dorado entregue la caja y el equipo figure como posible premio al abrirla.' },
        { testId: `${ref}:juego`, what: 'Prueba dentro del juego de un drop y un horario', how: 'Ver la lista de pruebas dentro del juego', record: 'Resultado registrado en inGameTests con fecha.' },
      )
      return
    }
    if (/coordenad|terreno|terrain|imagen|calibr/.test(t)) {
      // Todas las filas de coordenadas/imágenes/calibración se unen a la pregunta única COORDENADAS.
      attach(COORD_KEY, ref)
      return
    }
    if (/misi|quest/.test(t) && /\b7\b|\b8\b|\b9\b|7.?9/.test(t)) {
      attach('quest-unpublished:7-9', ref)
      return
    }
    if (/receta|chaos machine|mezcla|mix/.test(t)) {
      recipes.forEach((r) => (r.variants?.length ? r.variants.map((v) => `recipe:${r.id}:${v.id}`) : [`recipe:${r.id}`]).forEach((k) => appendKnown(k, tag)))
      return
    }
    if (/combo/.test(t) && appendKnown('combo:bk', tag)) return
    if (/npc/.test(t) && /coord|ubicaci|posici/.test(t)) {
      appendKnown(NPC_ORIGIN_KEY, tag)
      return
    }
    if (/recompensa|efecto|reward/.test(t) && /misi|quest/.test(t)) {
      ;['quest-reward:sebina', 'quest-reward:marlon', 'quest-reward:devin'].forEach((k) => appendKnown(k, tag))
      return
    }
    // Sistemas: cada fila se integra en la pregunta específica del sistema (sin mezclar verificaciones).
    const sysMatch = (
      [
        [/off.?helper|mu.?helper|\bhelper\b/, 'helper'],
        [/socket|harmony|\b380\b/, 'sistemas-avanzados'],
        [/master.?(skill|tree|level|nivel)|árbol master|arbol master/, 'master'],
        [/reset/, 'resets'],
        [/market|\bvip\b|pase|battle.?pass|cash.?shop/, 'market'],
        [/comando|command/, 'comandos'],
        [/mejora|upgrade|\+1[0-5]|\bluck\b|opci[oó]n adicional/, 'mejoras'],
        [/clase|class|habilidad|skill/, 'clases'],
      ] as [RegExp, string][]
    ).find(([re]) => re.test(t))
    if (sysMatch) {
      attach(`system:${sysMatch[1]}`, ref)
      return
    }
    add({ key: `control:${c.id}`, category: 'paquete', section: 'Paquete 08/10/2026', links: ['/guia/verificacion#paquete'], name: c.title ?? c.id, missing: c.text, known: `Declarado en details_json.pending_requirements de la fila dataset.${c.extra ? ` ${c.extra}` : ''}`, reason: 'falta-dato', question: controlQuestion(c.text), evidence: ['administración'] })
  })

  // ── 10. Sistemas: cada uno con su propia verificación; los archivos se piden una sola vez ──
  const sysFiles: [string, string][] = [
    ['helper', 'MU Helper / OffHelper (configuración del GameServer o del cliente)'],
    ['bonus', 'BonusManager: definición de bonus 1 y 2'],
    ['sistemas-avanzados', 'Socket, Harmony y opciones 380'],
    ['master', 'Árbol Master (MasterSkillTree)'],
    ['resets', 'Resets y Grand Reset'],
    ['market', 'Vanguard Market, VIP y Pase de Batalla'],
    ['comandos', 'Comandos activos'],
    ['mejoras', 'Tasas de mejora de ítems (+10 a +15, Luck, opción adicional)'],
    ['clases', 'Estadísticas iniciales, puntos por nivel y nivel de aprendizaje de habilidades'],
  ]
  add({
    key: SYSTEMS_SOURCE_KEY,
    category: 'sistema',
    section: 'Sistemas',
    links: ['/guia'],
    name: 'Archivos de configuración de sistemas (una sola solicitud)',
    missing: sysFiles.map(([, f]) => f).join(' · '),
    known: 'Ninguno de estos archivos está en el paquete del 08/10/2026. Cada sistema conserva su propia pregunta y estado; este es solo el pedido de archivos.',
    reason: 'falta-dato',
    question: `Enviar los archivos de: ${sysFiles.map(([, f]) => f).join('; ')}.`,
    evidence: ['configuración'],
    needsAdmin: true,
  })
  const sysRef = `Archivo pedido en ${SYSTEMS_SOURCE_KEY}.`
  const sys = (k: string, name: string, known: string, missing: string, question: string) =>
    add({ key: `system:${k}`, category: 'sistema', section: 'Sistemas', links: ['/guia'], name, missing, known: `${known} ${sysRef}`, reason: 'falta-dato', question, evidence: ['administración', 'configuración'] })
  sys('helper', 'MU Helper y OffHelper', 'Sin configuración en el paquete. Los datos de bonus (44/45) no documentan el Helper.', 'Acceso, requisitos, coste, límites, tiempo offline, desconexión/reconexión y restricciones', '¿Cómo se accede al MU Helper y al OffHelper en Vanguard, qué requisitos y coste tienen, qué límites aplican y qué pasa al desconectar o reconectar?')
  sys('bonus', 'Bonificaciones temporales (BonusManager)', 'Los horarios de 45_bonus_schedules apuntan a bonus 1 y 2, que no tienen definición en 44_bonus_definitions. La guía no anuncia ninguna bonificación activa.', 'Definición de bonus 1 y 2 (qué multiplican) y horarios completos', '¿Qué hacen los bonus 1 y 2 (experiencia, drop u otro, y cuánto) y cuáles son sus horarios vigentes?')
  sys('sistemas-avanzados', 'Socket, Harmony y opciones 380', 'Sin configuración en el paquete.', 'Si están activos y con qué reglas', '¿Están activos Socket, Harmony y las opciones 380 en Vanguard? Si lo están, ¿con qué reglas?')
  sys('master', 'Árbol Master', 'Sin configuración en el paquete.', 'Nivel o reset de acceso y puntos por nivel', '¿Desde qué nivel o reset se accede al árbol Master y cuántos puntos se obtienen?')
  sys('resets', 'Resets y Grand Reset', 'Sin configuración en el paquete.', 'Requisitos, qué se conserva y premios', '¿Requisitos de reset y Grand Reset, qué se conserva y qué premios da cada etapa?')
  sys('market', 'Vanguard Market, VIP y Pase de Batalla', `43_server_rates documenta niveles de cuenta${w.rates.length ? ` (${Array.from(new Set(w.rates.map((r) => r.accountName).filter(Boolean))).join(', ')})` : ''}; precios y contenido no están en el paquete.`, 'Contenido, moneda, precios y comisiones', '¿Qué ofrece hoy el Market, el VIP y el Pase de Batalla, en qué moneda y con qué comisiones?')
  sys('comandos', 'Comandos del juego', 'Sin configuración en el paquete.', 'Lista de comandos activos y su efecto', '¿Qué comandos están activos y qué hace cada uno?')
  sys('mejoras', 'Joyas y mejoras de ítems', 'Sin tasas de mejora en el paquete.', 'Tasas +10 a +15, pérdida al fallar, Luck y opción adicional', '¿Tasas de éxito de +10 a +15, qué se pierde al fallar y cómo influyen Luck y la opción adicional?')
  // Clases: hay información parcial publicada. Solo las active = True se presentan como disponibles.
  const skillItemCount = Array.from(w.items.keys()).filter((id) => isSkillItem(w, id)).length
  const activeClassNames = Array.from(w.activeClassIds).map((id) => w.classNames.get(id) ?? id)
  const inactiveClassCount = w.classNames.size - w.activeClassIds.size
  sys(
    'clases',
    'Clases y habilidades',
    `Documentado: 04_classes tiene ${w.classNames.size} registros; ${w.activeClassIds.size} con active = True (${activeClassNames.join(', ')}), que son las clases activas que publica la guía${inactiveClassCount ? `; las otras ${inactiveClassCount} no se presentan como disponibles` : ''}. Compatibilidad clase–ítem de ${w.itemClasses.size} ítems en 05_item_classes; ${skillItemCount} ítems que enseñan habilidades, con sus requisitos de ítem cuando figuran en 01_items.`,
    'Estadísticas iniciales, puntos por nivel y nivel de aprendizaje de cada habilidad',
    '¿Qué estadísticas iniciales y puntos por nivel tiene cada clase, y desde qué nivel se aprende cada habilidad?',
  )
  // Mascotas: vías documentadas publicadas; las recetas siguen en sus propias claves.
  const petItems = [
    ...['13,31@0', '13,31@1', '13,53'],
    ...Array.from(w.items.values()).filter((it) => /^horn of fenrir$/i.test(it.name)).map((it) => it.id),
  ]
  const petRoutes = petItems.map((id) => {
    const ss = resolveItemSources(w, id).filter((s) => !s.blocked)
    return `${itemName(w, id)} (${id}): ${ss.length ? `${ss.length} vía(s) — ${list(Array.from(new Set(ss.map((s) => s.label ?? s.title))), 3)}` : 'sin vía en los archivos'}`
  })
  const petRecipeKeys = recipes.filter((r) => r.group === 'mascotas').flatMap((r) => (r.variants?.length ? r.variants.map((v) => `recipe:${r.id}:${v.id}`) : [`recipe:${r.id}`]))
  solvedFromData.push(`Mascotas: vías de obtención publicadas con sus condiciones en las fichas y en /guia/combinaciones — ${petRoutes.join(' | ')}. Las recetas siguen pendientes en sus claves: ${petRecipeKeys.join(', ')}.`)

  // ── 11. COORDENADAS: una sola pregunta con posiciones, imágenes y calibración en estados separados ──
  const imgs = extras.mapImages ? Array.from(extras.mapImages.values()) : undefined
  const declared = (imgs ?? []).filter((i) => [i.horizontal, i.vertical].includes('X') && [i.horizontal, i.vertical].includes('Y'))
  const undeclared = (imgs ?? []).filter((i) => !declared.includes(i))
  const axisText = (i: MapImage) => `${mapName(i.mapId)}: horizontal = ${i.horizontal}, vertical = ${i.vertical}`
  add({
    key: COORD_KEY,
    category: 'mapa',
    section: 'Mapas',
    links: ['/guia/mapas', '/guia/mapas/lorencia'],
    name: 'COORDENADAS: posiciones, imágenes y calibración',
    missing: 'Solo la comprobación visual, mapa por mapa, del origen (0,0), el sentido de cada eje y la escala de las marcas',
    known: [
      `Posiciones: 08_spawns (spots) y 32_event_spawn_rules (reglas de invasión) contienen X/Y; 09_monster_locations documenta relaciones y condiciones de presencia, sin X/Y. Las posiciones de NPC se piden en ${NPC_ORIGIN_KEY}.`,
      imgs
        ? `Imágenes: ${imgs.length} mapas con imagen en 47_map_images. Ejes declarados por mapa en coordinate_horizontal / coordinate_vertical (se leen por mapa, sin suponer X horizontal): ${list(declared.map(axisText), 8)}.${undeclared.length ? ` Sin ejes declarados o no cuadradas (sin marcas): ${list(undeclared.map((i) => mapName(i.mapId)), 8)}.` : ''}`
        : 'Imágenes: 47_map_images no se pudo leer para este informe.',
      'Calibración: pendiente la comprobación visual en cada mapa; un punto o una captura de un mapa no valida los demás.',
    ].join(' '),
    reason: 'falta-dato',
    question: 'Para cada mapa con imagen: una captura con 2–3 puntos de X/Y conocidos para comprobar origen, sentido y escala. Se registra mapa por mapa.',
    evidence: ['captura', 'prueba en el juego'],
    needsAdmin: true,
  })

  // ── 12. Solicitud técnica única al servidor (recetas, efectos de misiones, combo, posiciones en eventos) ──
  const serverData = out.filter((p) => p.source === SERVER_SOURCE_KEY)
  if (serverData.length) {
    add({
      key: SERVER_SOURCE_KEY,
      category: 'paquete',
      section: 'Solicitudes de archivos',
      links: ['/guia/verificacion#pendientes'],
      name: `${sourceLabel[SERVER_SOURCE_KEY]} (una sola solicitud técnica · ${serverData.length} datos)`,
      missing: 'Archivos o código del servidor de: mezclas de la Chaos Machine (ingredientes, Zen, éxito, fallo, VIP) y empaquetado de joyas · efectos y recompensas de misiones · secuencia y tiempos del combo · posiciones o reglas de aparición dentro de eventos',
      known: 'Ninguno de los 47 CSV contiene estos datos. Cada dato conserva su propio estado en la lista de esta solicitud; no se convierten en preguntas manuales a administración.',
      reason: 'falta-dato',
      question: 'Enviar la configuración del servidor de mezclas (ChaosMix), misiones (efectos/recompensas), combo y spawns de eventos.',
      evidence: ['configuración'],
      needsAdmin: true,
    })
  }

  deferred.forEach((d) => {
    const p = out.find((x) => x.key === d.into)
    if (!p) return
    if (d.ref) p.refs = Array.from(new Set([...(p.refs ?? []), d.ref]))
    if (d.text) p.known += ` ${d.text}`
  })

  out.sort((a, b) => Number(b.needsAdmin) - Number(a.needsAdmin) || categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category))

  // ── Comprobaciones en la página (staff, sin entrar al juego) ─────────────
  const pageChecks: ProposedTest[] = [
    { testId: 'page:map-markers', what: 'Marcadores sobre la imagen de cada mapa (ejes declarados en 47_map_images)', how: 'En cada ficha de mapa con imagen, comparar 3 spots con sus X/Y escritas; los ejes se leen por mapa (Lorencia: horizontal = Y, vertical = X)', record: 'Mapa por mapa: si origen, sentido y escala coinciden (sí/no). Un mapa no valida los demás.' },
    { testId: 'page:search-alas', what: 'Buscador: "alas"', how: 'Escribir "alas" en el buscador de la guía', record: 'Primer resultado (debe ser la guía de alas).' },
    { testId: 'page:search-barracks', what: 'Buscador: "cómo entro a Barracks"', how: 'Escribir la frase en el buscador', record: 'Primer resultado (deben ser los accesos y requisitos de Barracks).' },
    { testId: 'page:recipe-links', what: 'Resultados de recetas enlazados por ID', how: 'Abrir /guia/alas/alas-evo-3 y tocar Wing of Storm', record: 'Si abre la ficha 12,36.' },
  ]

  // ── Pruebas dentro del juego ─────────────────────────────────────────────
  const bull = Array.from(w.monsters.values()).find((m) => /bull fighter/i.test(m.name))
  const bullSpawn = bull ? (w.idx.spawnsByMonster.get(bull.id) ?? []).find((s) => s.kind !== 'invasion') : undefined
  const q0 = buildQuestView(w, 0)
  const tests: ProposedTest[] = [
    { testId: 'game:spot-bull-fighter', what: `Ubicación de ${bull?.name ?? 'Bull Fighter'}`, how: bullSpawn ? `Ir a ${mapName(bullSpawn.mapId)} X ${bullSpawn.xMin ?? '?'}–${bullSpawn.xMax ?? '?'} / Y ${bullSpawn.yMin ?? '?'}–${bullSpawn.yMax ?? '?'}` : 'Ir al mapa de su ficha', record: 'Si aparece ahí (sí/no) y captura con coordenadas.' },
    { testId: 'game:quest-0-start', what: 'Inicio de Sebina, primera parte', how: `Hablar con Sebina con nivel ${q0?.levelMin ?? '(del archivo)'}${q0?.zen !== undefined ? ` y ${q0.zen.toLocaleString('es-ES')} Zen` : ''}`, record: 'Nivel y Zen que pide de verdad, mapa y X/Y de Sebina.' },
    { testId: 'game:quest-0-drop', what: 'Scroll of the Emperor con la misión activa', how: 'Cazar un monstruo de los listados en la guía con la misión aceptada', record: 'Monstruo, mapa y si salió.' },
    { testId: 'game:item-jewel-of-chaos', what: 'Drop normal de Jewel of Chaos', how: 'Cazar en un spot de su ficha', record: 'Monstruo, mapa y si salió (sin probabilidades).' },
    { testId: 'game:barracks-access', what: 'Acceso a Barracks', how: 'Intentar entrar con y sin la misión de Devin', record: 'Requisito real y desde dónde se entra.' },
    { testId: 'game:recipe-alas-evo-1', what: 'Mezcla de alas Evo 1', how: 'Colocar la combinación clásica en la Chaos Machine sin confirmar', record: 'Si la acepta, Zen y % mostrados (captura).' },
    { testId: 'game:event-schedule', what: 'Horario de un evento con horario configurado', how: 'Comparar la tecla H con /guia/eventos', record: 'Evento, hora en la guía y hora real.' },
  ]

  const pub = w.publication
  const catalog = {
    raw: pub.rawItems,
    published: w.items.size,
    excluded: pub.excludedItems,
    noRoutesPublished: noRoutes.length,
    noRoutesExcluded: pub.excludedItems.filter((e) => e.routes === 0).length,
  }

  const coverage: SectionCoverage[] = [
    { section: 'NPC', reviewed: true, note: 'Cruzado con 06_monsters y 08_spawns.' },
    { section: 'Misiones 0–6 y evoluciones', reviewed: w.quests.quests.size > 0, note: w.quests.quests.size ? 'Cruzado con 36–42.' : 'NO REVISADO: archivos de misiones no cargados.' },
    { section: 'Ítems, drops y cajas', reviewed: w.routes.length > 0, note: w.routes.length ? `${w.items.size} ítems publicados con la lógica de las fichas.` : 'NO REVISADO: rutas no cargadas.' },
    { section: 'Apariciones en eventos (31, 32)', reviewed: !!extras.eventMonsters && !!extras.eventSpawnRules, note: extras.eventMonsters && extras.eventSpawnRules ? `${evMon.length} filas de 31 y ${evRules.length} de 32.` : 'NO REVISADO: 31 o 32 no se pudieron leer.' },
    { section: 'Invasiones', reviewed: true, note: 'Contraste con la información aprobada.' },
    { section: 'Eventos (27–34)', reviewed: w.events.size > 0, note: 'Actividad, tipo de activación y horario.' },
    { section: 'Accesos a mapas (07_maps)', reviewed: w.maps.size > 0, note: 'minimum_move_level y move_entries.' },
    { section: 'Recetas, alas y capas', reviewed: true, note: 'Identidad de ingredientes y resultados; Zen, éxito y fallo no están en el paquete.' },
    { section: 'Pendientes de 00_control.csv', reviewed: extras.controlPending !== undefined, note: extras.controlPending !== undefined ? `${ctl.length} declarados, consolidados con los existentes.` : 'NO REVISADO: no se pudo leer la fila dataset.' },
    { section: 'Marcadores sobre imágenes de mapa', reviewed: false, note: 'Requiere comprobación visual en la página (lista aparte).' },
  ]

  tests.push(...controlTests)
  return { pendings: out, resolvedByAdmin, resolvedByAudit, solvedFromData, dataChecks, npcStatus, pageChecks, tests, catalog, coverage, extrasErrors: extras.errors, annex: { noRoutes, reviewItems, coordItems, partial: Array.from(byReason.values()), ordinaryGaps, byEvent: Array.from(byEvent.entries()) } }
}

type ControlPending = { id: string; title?: string; text: string; extra?: string }

function normalizeControlPending(v: unknown): ControlPending[] {
  if (v === undefined || v === null) return []
  const fromObj = (o: Record<string, unknown>, fallback: string): ControlPending => {
    const pick = (...ks: string[]) => ks.map((k) => o[k]).find((x) => typeof x === 'string' && x) as string | undefined
    const text = pick('description', 'requirement', 'text', 'what', 'detail', 'details', 'missing', 'note') ?? JSON.stringify(o)
    const extraKeys = Object.keys(o).filter((k) => !['id', 'key', 'code', 'name', 'title', 'description', 'requirement', 'text', 'what', 'detail', 'details', 'missing', 'note'].includes(k))
    return { id: pick('id', 'key', 'code') ?? fallback, title: pick('title', 'name'), text, extra: extraKeys.length ? extraKeys.map((k) => `${k}: ${typeof o[k] === 'string' ? o[k] : JSON.stringify(o[k])}`).join(' · ') : undefined }
  }
  if (Array.isArray(v)) return v.map((x, i) => (typeof x === 'string' ? { id: `#${i + 1}`, text: x } : typeof x === 'object' && x ? fromObj(x as Record<string, unknown>, `#${i + 1}`) : { id: `#${i + 1}`, text: String(x) }))
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([k, x]) => (typeof x === 'string' ? { id: k, text: x } : typeof x === 'object' && x ? fromObj(x as Record<string, unknown>, k) : { id: k, text: JSON.stringify(x) }))
  return [{ id: '#1', text: String(v) }]
}

function controlQuestion(text: string): string {
  const t = text.toLowerCase()
  if (/vigen|desplegad|deploy|activa en producci/.test(t)) return '¿La configuración exportada el 08/10/2026 es la que está desplegada hoy en el servidor? Si cambió algo después, ¿qué?'
  if (/variante|variant/.test(t) && /misi|quest/.test(t)) return '¿Qué nombre ve el jugador para cada variante de los objetos de misión (por ejemplo @0 y @1 de un mismo ítem)?'
  if (/bonus|temporal|happy|doble/.test(t)) return '¿Qué bonus temporales están activos hoy, en qué horario y qué multiplican?'
  if (/terreno|terrain|imagen|map image|coordenad/.test(t)) return '¿Las imágenes de terreno corresponden a los mapas actuales del servidor y usan la misma orientación de coordenadas?'
  return `Declarado pendiente en el paquete: "${text}". ¿Cuál es el dato vigente?`
}

export type GuidePendings = ReturnType<typeof buildGuidePendings>

export function formatGuidePendings(r: GuidePendings): string {
  const cell = (s: string) => s.replace(/\s+/g, ' ').replace(/\|/g, '/')
  const admin = r.pendings.filter((p) => p.needsAdmin)
  const internal = r.pendings.filter((p) => !p.needsAdmin)
  const byCat = categoryOrder.map((c) => [c, admin.filter((p) => p.category === c && !p.source).length] as const).filter(([, n]) => n)
  const requests = admin.filter((p) => p.key.startsWith('source:'))
  const sourced = admin.filter((p) => p.source)
  const decisions = admin.filter((p) => p.kind !== 'archivo')
  const head = ['| # | Sección y enlace | Nombre | Falta | Se sabe | Estado | Pregunta | Evidencia | Clave (y referencias) |', '|---|---|---|---|---|---|---|---|---|']
  const requestBlock = (req: Pending, i: number): string[] => {
    const items = sourced.filter((p) => p.source === req.key)
    return [
      `### ${i + 1}. ${req.name} [${req.key}]${req.refs?.length ? ` (+ ${req.refs.join(', ')})` : ''}`,
      `Pedir: ${req.question}`,
      `Se sabe: ${req.known}`,
      ...(items.length
        ? [`Datos que resuelve (${items.length}), cada uno con su estado:`, ...items.map((p) => `- ${p.name} [${p.key}${p.refs?.length ? `; también ${p.refs.join(', ')}` : ''}] · Falta: ${cell(p.missing)} · Se sabe: ${cell(p.known)} · Estado: ${reasonLabel[p.reason]}`)]
        : []),
      '',
    ]
  }
  const row = (p: Pending, i: number): string => `| ${i + 1} | ${cell(p.section)} · ${cell(p.links.join(' · '))} | ${cell(p.name)} | ${cell(p.missing)} | ${cell(p.known)} | ${reasonLabel[p.reason]} | ${cell(p.question)} | ${p.evidence.join(', ')} | ${p.key}${p.refs?.length ? ` (+ ${p.refs.join(', ')})` : ''} |`
  const lines = [
    'PENDIENTES DE LA GUÍA MU VANGUARD',
    `Calculado: ${new Date().toLocaleString('es-ES')} · exportación del ${EXPORT_DATE_LABEL}`,
    `PENDIENTES: ${internal.length} correcciones internas · ${requests.length} solicitudes de archivos (que resuelven ${sourced.length} datos) · ${decisions.length} decisiones de administración · ${r.pageChecks.length + r.tests.length} pruebas`,
    `Por tema (archivos + decisiones): ${byCat.map(([c, n]) => `${categoryLabel[c]} ${n}`).join(' · ')}`,
    `Problemas internos (los resuelvo yo): ${internal.length} · Respondidos por administración: ${r.resolvedByAdmin.length} · Resueltos con la auditoría técnica: ${r.resolvedByAudit.length} · Pruebas en el juego registradas: ${inGameTests.length}`,
    ...(r.extrasErrors.length ? ['', 'ERRORES DE LECTURA DEL INFORME:', ...r.extrasErrors.map((e) => `- ${e}`)] : []),
    '',
    '## CATÁLOGO',
    `01_items: ${r.catalog.raw} ítems · publicados: ${r.catalog.published} · excluidos por filtro de publicación: ${r.catalog.excluded.length}`,
    ...r.catalog.excluded.map((e) => `  - ${e.id}\t${e.name}\t${e.reason} · rutas originales: ${e.routes}`),
    `Sin ruta de drop: ${r.catalog.noRoutesPublished} publicados + ${r.catalog.noRoutesExcluded} excluidos. "Sin ruta de drop" ≠ "no disponible en el servidor" (eso solo se marca con confirmación).`,
    '',
    '## SECCIONES REVISADAS',
    ...r.coverage.map((c) => `- [${c.reviewed ? 'revisada' : 'NO revisada'}] ${c.section}: ${c.note}`),
    '',
    '## RESUELTO CON LOS ARCHIVOS ACTUALES',
    ...(r.solvedFromData.length ? r.solvedFromData.map((s) => `- ${s}`) : ['- nada nuevo']),
    '',
    `## RESUELTO CON LA ${TECHNICAL_AUDIT_LABEL.toUpperCase()} (no es confirmación de administración ni prueba en el juego)`,
    ...(r.resolvedByAudit.length
      ? r.resolvedByAudit.map((x) => `- ${x.p.name} [${x.p.key}]\n  Error corregido: ${x.error}\n  Lectura correcta: ${x.summary}\n  Retirado de pendientes por la auditoría técnica; no comprobado en el navegador ni dentro del juego.`)
      : ['- ninguno']),
    '',
    '## COMPROBACIONES DE DATOS',
    ...r.dataChecks.map((s) => `- ${s}`),
    '',
    `## 1. ${kindLabel.interna.toUpperCase()} (${internal.length}) — no requieren respuesta`,
    ...(internal.length ? internal.map((p) => `- ${p.name}: ${p.known} (clave ${p.key}${p.refs?.length ? `; también ${p.refs.join(', ')}` : ''})`) : ['- ninguna']),
    '',
    `## 2. ${kindLabel.archivo.toUpperCase()} — ${requests.length} solicitudes, cada fuente se pide una sola vez`,
    ...requests.flatMap(requestBlock),
    ...(sourced.filter((p) => !requests.some((q) => q.key === p.source)).length
      ? ['Datos sin solicitud propia:', ...sourced.filter((p) => !requests.some((q) => q.key === p.source)).map((p) => `- ${p.name} [${p.key}] · ${cell(p.missing)}`)]
      : []),
    '',
    `## 3. ${kindLabel.administracion.toUpperCase()} (${decisions.length})`,
    ...head,
    ...decisions.map(row),
    '',
    '## RESPONDIDO POR ADMINISTRACIÓN',
    ...(r.resolvedByAdmin.length ? r.resolvedByAdmin.map((x) => `- ${x.p.name}: ${x.answer} (Confirmado por administración, ${formatAdminDate(x.date)})`) : ['- ninguno todavía']),
    '',
    '## 4. PRUEBAS PENDIENTES',
    '### En la página (staff, sin entrar al juego)',
    ...r.pageChecks.map((t, i) => `${i + 1}. ${t.what}\n   Cómo: ${t.how}\n   Registrar: ${t.record}\n   Clave: ${t.testId}`),
    '',
    '### En el navegador y dentro del juego (documentado, aún no probado)',
    ...r.tests.map((t, i) => {
      const done = inGameTest(t.testId)
      return `${i + 1}. ${t.what} [${done ? `HECHA ${done.date}: ${done.result} — ${done.observed}` : 'pendiente'}]\n   Cómo: ${t.how}\n   Registrar: ${t.record}\n   Clave: ${t.testId}`
    }),
    '',
    '## ANEXO A — Ítems publicados sin ruta de drop',
    ...r.annex.noRoutes.map((s) => `- ${s}`),
    '',
    '## ANEXO B — Ítems en revisión (objetivos documentados, publicación retenida)',
    ...r.annex.reviewItems.map((s) => `- ${s}`),
    '',
    `## ANEXO C — ${r.annex.coordItems.length} ítems con monstruo y mapa documentados: solo faltan coordenadas o reglas de aparición`,
    ...r.annex.coordItems.map((s) => `- ${s}`),
    '',
    '## ANEXO D — Otros ítems con información parcial, por motivo',
    ...r.annex.partial.flatMap((g) => [`- ${g.ids.length} × ${g.reason}`, `    ${g.names.map((n, i) => `${n} (${g.ids[i]})`).join(', ')}`]),
    '',
    '## ANEXO E — Monstruo + mapa sin posición',
    ...r.annex.byEvent.flatMap(([eid, e]) => [`- Evento ${eid}${e.inactive ? ' (inactivo en la configuración)' : ''}:`, ...e.gaps.map((g) => `    ${g.monster} en ${g.map}: ${Array.from(g.items).slice(0, 6).join(', ')}`)]),
    ...r.annex.ordinaryGaps.map((g) => `- Sin evento: ${g.monster} en ${g.map}: ${Array.from(g.items).slice(0, 6).join(', ')}`),
    '',
    `## ANEXO F — ${r.npcStatus.length} NPC publicados: ubicación, función e inventario (estados independientes)`,
    ...r.npcStatus.map((n) => `- ${n.id}\t${n.name}\tubicación: ${n.location}\tfunción: ${n.function}\tinventario: ${n.inventory}`),
    '',
    'Cómo responder: indica la clave y la respuesta. Se registra como "Confirmado por administración" con fecha y solo confirma ese dato.',
  ]
  return lines.join('\n')
}
